/**
 * Public payment page — `/pay/:token` (UNAUTHENTICATED).
 *
 * A client opens this from the quote link with no portal login. It fetches the
 * pay-session from Mack's public `getPaySession` onRequest HTTP function and
 * renders the AV-green checkout (mockup: scratchpad/av-payment-page.html):
 * brand header, amount + gst note, then the method cards the backend returns
 * (UPI + QR, Razorpay/PayPal CTAs, bank details, coming-soon). The backend owns
 * all money / firm / method logic — this page renders only what it returns.
 *
 * `PayView` is the pure presentation (a PaySession in → the page out) so the
 * dev harness can render it with mock sessions and no backend.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import qrcode from 'qrcode-generator';
import {
  fetchPaySession,
  methodHelper,
  methodLabel,
  methodShowsSurcharge,
  upiAppLinks,
  type BankMethod,
  type PayMethod,
  type PaypalMethod,
  type PaySession,
  type PayTerminal,
  type RazorpayMethod,
  type UpiMethod,
} from '../lib/paySession';
import '../styles/pay.css';

/* ════════════════════ Brand header (shared by page + state screens) ════════════════════ */

function PayHeader({ quoteRef, clientName }: { quoteRef?: string; clientName?: string }) {
  return (
    <div className="pp-header">
      <div className="pp-header-top">
        <div className="pp-wordmark">
          AAYATAN VEDA
          <span className="wm-sub">Vastu &amp; Architecture</span>
        </div>
        <span className="pp-secure">
          <span aria-hidden="true">🔒</span> Secure payment
        </span>
      </div>
      <div className="pp-for">Payment for your quotation</div>
      {(quoteRef || clientName) && (
        <div className="pp-quote">
          {quoteRef}
          {quoteRef && clientName ? ' · ' : ''}
          {clientName}
        </div>
      )}
    </div>
  );
}

/* ════════════════════ QR (inline, offline, high-contrast) ════════════════════ */

function PayQr({ text, label }: { text: string; label: string }) {
  const { dim, path } = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const count = qr.getModuleCount();
    const margin = 2;
    let d = '';
    for (let r = 0; r < count; r++) {
      for (let c = 0; c < count; c++) {
        if (qr.isDark(r, c)) d += `M${c + margin} ${r + margin}h1v1h-1z`;
      }
    }
    return { dim: count + margin * 2, path: d };
  }, [text]);
  return (
    <svg viewBox={`0 0 ${dim} ${dim}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect width={dim} height={dim} fill="var(--pp-paper)" />
      <path d={path} fill="var(--pp-ink)" />
    </svg>
  );
}

/* ════════════════════ Copy button (announces to a shared live region) ════════════════════ */

function CopyButton({ value, what, announce }: { value: string; what: string; announce: (m: string) => void }) {
  const [copied, setCopied] = useState(false);
  const t = useRef<number | undefined>(undefined);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* clipboard blocked (e.g. insecure context) — the value is still visible */
    }
    setCopied(true);
    announce(`${what} copied`);
    window.clearTimeout(t.current);
    t.current = window.setTimeout(() => setCopied(false), 1600);
  }
  useEffect(() => () => window.clearTimeout(t.current), []);
  return (
    <button type="button" className="copy-btn" data-copied={copied} onClick={copy} aria-label={`Copy ${what}`}>
      <span aria-hidden="true">{copied ? '✓' : '⧉'}</span>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/* ════════════════════ Method icon glyph ════════════════════ */

const METHOD_ICON: Record<PayMethod['type'], string> = {
  upi: '▲',
  razorpay: '▭',
  paypal: 'P',
  bank: '🏦',
  stripe: 'S',
};

/* ════════════════════ Method cards ════════════════════ */

function MethodCard({
  m,
  session,
  announce,
}: {
  m: PayMethod;
  session: PaySession;
  announce: (msg: string) => void;
}) {
  const isPrimary = m.type === 'upi' || m.type === 'razorpay' || m.type === 'paypal';
  const disabled = m.type === 'stripe';

  return (
    <div className={`m-card${isPrimary ? ' primary' : ''}${disabled ? ' soon' : ''}`}>
      <div className="m-top">
        <div className="m-ico" aria-hidden="true">{METHOD_ICON[m.type]}</div>
        <div className="m-meta">
          <div className="m-label">{methodLabel(m, session.currency)}</div>
          <div className="m-help">{methodHelper(m, session.currency)}</div>
        </div>
        {disabled && <div className="m-badge">Coming soon</div>}
      </div>
      {!disabled && (
        <div className="m-expand">
          {methodShowsSurcharge(m) && <p className="m-fee">{m.surchargeNote}</p>}
          <MethodBody m={m} session={session} announce={announce} />
        </div>
      )}
    </div>
  );
}

/** A processor rail whose link the CF could not mint this request. */
function UnavailableNote({ what }: { what: string }) {
  return (
    <p className="m-fee" role="status">
      {what} is momentarily unavailable — please try another method, or refresh in a minute.
    </p>
  );
}

function MethodBody({
  m,
  session,
  announce,
}: {
  m: PayMethod;
  session: PaySession;
  announce: (msg: string) => void;
}) {
  switch (m.type) {
    case 'upi':
      return <UpiBody m={m} session={session} announce={announce} />;
    case 'razorpay': {
      const rz = m as RazorpayMethod;
      if (rz.unavailable || !rz.payUrl) return <UnavailableNote what="Card checkout" />;
      return (
        <a className="m-cta" href={rz.payUrl} target="_blank" rel="noopener noreferrer">
          Pay {rz.payAmountDisplay} <span className="arr" aria-hidden="true">→</span>
        </a>
      );
    }
    case 'paypal': {
      const pp = m as PaypalMethod;
      if (pp.unavailable || !pp.approveUrl) return <UnavailableNote what="PayPal" />;
      return (
        <a className="m-cta" href={pp.approveUrl} target="_blank" rel="noopener noreferrer">
          Pay {pp.payAmountDisplay} <span className="arr" aria-hidden="true">→</span>
        </a>
      );
    }
    case 'bank':
      return <BankBody m={m as BankMethod} announce={announce} />;
    default:
      return null;
  }
}

function UpiBody({
  m,
  session,
  announce,
}: {
  m: UpiMethod;
  session: PaySession;
  announce: (msg: string) => void;
}) {
  const apps = useMemo(() => upiAppLinks(m.qrPayload), [m.qrPayload]);
  return (
    <>
      <div className="upi-row">
        <div className="upi-qr">
          <PayQr text={m.qrPayload} label={`UPI QR to pay ${session.firmName}`} />
        </div>
        <div className="upi-info">
          <div className="ui-t">Scan with any UPI app, or pay to:</div>
          <div className="upi-id-row">
            <span className="upi-id">{m.upiId}</span>
            <CopyButton value={m.upiId} what="UPI ID" announce={announce} />
          </div>
        </div>
      </div>
      <div className="upi-apps-label">Open your app — amount pre-filled</div>
      <div className="upi-apps">
        {apps.map((a) => (
          <a key={a.key} className="upi-app" href={a.href} aria-label={`Pay with ${a.label}`}>
            <span className={`ua-badge ${a.key}`} aria-hidden="true">
              {a.key === 'gpay' ? 'G' : a.key === 'phonepe' ? 'Pe' : 'P'}
            </span>
            {a.label}
          </a>
        ))}
      </div>
      <div className="upi-helper">Tap your UPI app or scan the QR — instant. On a laptop, scan the QR from your phone.</div>
    </>
  );
}

function BankBody({ m, announce }: { m: BankMethod; announce: (msg: string) => void }) {
  const f = m.fields || {};
  const rows: { k: string; v?: string | null; copy?: boolean }[] = [
    { k: 'A/c name', v: f.acName },
    { k: 'Bank', v: f.bank },
    { k: 'A/c no', v: f.acNo, copy: true },
    { k: 'IFSC', v: f.ifsc, copy: true },
    { k: 'SWIFT', v: f.swift, copy: true },
    { k: 'AD code', v: f.adCode, copy: true },
  ].filter((r) => r.v);
  return (
    <>
      <div className="bank-rows">
        {rows.map((r) => (
          <div className="bank-row" key={r.k}>
            <span className="bk">{r.k}</span>
            <span className="bv-wrap">
              <span className={`bv${r.copy ? ' mono' : ''}`}>{r.v}</span>
              {r.copy && r.v && <CopyButton value={r.v} what={r.k} announce={announce} />}
            </span>
          </div>
        ))}
      </div>
      <div className="bank-note">After transferring, our team confirms it against your quote.</div>
    </>
  );
}

/* ════════════════════ The page ════════════════════ */

export function PayView({ session }: { session: PaySession }) {
  const [live, setLive] = useState('');
  const hasInstant = session.methods.some(
    (m) => m.type === 'upi' || m.type === 'razorpay' || m.type === 'paypal',
  );
  const hasBank = session.methods.some((m) => m.type === 'bank');
  return (
    <div className="pp-root">
      <div className="pp-card">
        <PayHeader quoteRef={session.quoteRef} clientName={session.clientName} />

        <div className="pp-amount-block">
          {/* the page's primary heading (SR structure) — styled as the small
              mono label, semantically an h1 (Vera QA fix). */}
          <h1 className="pp-amount-label">Amount due</h1>
          <div className="pp-amount">{session.amountDisplay}</div>
          {session.clientName && <div className="pp-amount-name">Billed to {session.clientName}</div>}
          {session.gstNote && <div className="pp-tax-note">{session.gstNote}</div>}
          {session.gstin && <div className="pp-gstin">GSTIN: {session.gstin}</div>}
        </div>

        <div className="pp-body">
          <div className="pp-choose">Choose how you'd like to pay</div>
          {session.methods.map((m, i) => (
            <MethodCard key={`${m.type}-${i}`} m={m} session={session} announce={setLive} />
          ))}
          <div className="pp-foot">
            <span className="lk" aria-hidden="true">🔒</span>
            <span>
              {hasInstant ? 'Pay by UPI, card, Razorpay or PayPal and your quotation is confirmed instantly. ' : ''}
              {hasBank ? 'Bank transfers are confirmed by our team on receipt.' : ''}
            </span>
          </div>
        </div>
      </div>
      <p className="pp-caption">One secure link — every way to pay, pre-filled with your amount.</p>
      <div className="pp-sr" role="status" aria-live="polite">{live}</div>
    </div>
  );
}

/* ════════════════════ Terminal + loading states ════════════════════ */

const STATE_COPY: Record<PayTerminal, { ico: string; title: string; msg: string }> = {
  expired: {
    ico: '⏱',
    title: 'This payment link has expired',
    msg: 'For a fresh link, reply to your quotation message and our team will send a new one right away.',
  },
  already_paid: {
    ico: '✓',
    title: 'This quotation is already paid',
    msg: 'Thank you — your payment is received and your quotation is confirmed. Nothing more to do here.',
  },
  not_payable: {
    ico: '🌿',
    title: 'Not set up for online payment',
    msg: 'This quotation is being handled directly — our team will confirm it with you. No online payment is needed here.',
  },
  notfound: {
    ico: '🔎',
    title: 'Link not found',
    msg: "We couldn't find this payment link. Please check the link in your quotation message, or reply to it and we'll help.",
  },
  error: {
    ico: '🌿',
    title: "We couldn't load this page",
    msg: 'Something went wrong on our side. Please try again in a moment, or reply to your quotation message.',
  },
};

export function PayStateScreen({ status }: { status: PayTerminal }) {
  const s = STATE_COPY[status];
  return (
    <div className="pp-root">
      <div className="pp-card">
        <PayHeader />
        <div className="pp-state">
          <div className="pp-state-ico" aria-hidden="true">{s.ico}</div>
          <h1 className="pp-state-title">{s.title}</h1>
          <p className="pp-state-msg">{s.msg}</p>
        </div>
      </div>
    </div>
  );
}

function PayLoading() {
  return (
    <div className="pp-root">
      <div className="pp-card">
        <PayHeader />
        <div className="pp-loading" role="status">Loading your payment page…</div>
      </div>
    </div>
  );
}

/* ════════════════════ Route ════════════════════ */

export default function Pay() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<
    { phase: 'loading' } | { phase: 'ok'; session: PaySession } | { phase: 'terminal'; status: PayTerminal }
  >({ phase: 'loading' });

  useEffect(() => {
    let live = true;
    if (!token) {
      setState({ phase: 'terminal', status: 'notfound' });
      return;
    }
    setState({ phase: 'loading' });
    fetchPaySession(token).then((r) => {
      if (!live) return;
      setState(r.kind === 'ok' ? { phase: 'ok', session: r.session } : { phase: 'terminal', status: r.status });
    });
    return () => {
      live = false;
    };
  }, [token]);

  if (state.phase === 'loading') return <PayLoading />;
  if (state.phase === 'terminal') return <PayStateScreen status={state.status} />;
  return <PayView session={state.session} />;
}
